import {
  buildLocationSlotIndex,
  locationSlotKey,
  matchSlotForYardSelection,
} from "./yardLocationsUtils";

export const YARD_SLOT_STATUS = {
  EMPTY: "empty",
  OCCUPIED: "occupied",
  UNAVAILABLE: "unavailable",
};

const norm = (s) => String(s ?? "").trim().toLowerCase();

function normComparable(s) {
  return norm(s)
    .replace(/^section\s*/i, "")
    .replace(/^bay\s*/i, "")
    .replace(/^dock\s*/i, "")
    .replace(/\s+/g, " ");
}

function labelsMatch(a, b) {
  const x = normComparable(a);
  const y = normComparable(b);
  if (!x || !y) return false;
  return x === y;
}

/** Parse "Section: A · Bay: 1 · Dock: 2" from check-in location labels. */
export function parseTrailerLocationLabel(location) {
  const out = { sectionName: "", bayName: "", dockName: "" };
  if (!location) return out;
  for (const seg of String(location).split("·")) {
    const part = seg.trim();
    if (/^section:/i.test(part)) out.sectionName = part.replace(/^section:\s*/i, "").trim();
    if (/^bay:/i.test(part)) out.bayName = part.replace(/^bay:\s*/i, "").trim();
    if (/^dock:/i.test(part)) out.dockName = part.replace(/^dock:\s*/i, "").trim();
  }
  return out;
}

export function formatSlotCardLabel(slotType, name) {
  const label = String(name ?? "").trim();
  if (!label) return slotType === "DOCK" ? "DOCK" : "BAY";
  const upper = label.toUpperCase();
  if (slotType === "DOCK") {
    return upper.startsWith("DOCK") ? upper : `DOCK ${upper}`;
  }
  return upper.startsWith("BAY") ? upper : `BAY ${upper}`;
}

/** Combined bay + dock label for yard map cards. */
export function formatBayDockCardLabel(bayName, dockName) {
  const bay = String(bayName ?? "").trim();
  const dock = String(dockName ?? "").trim();
  if (bay && dock) return `${bay} ${dock}`;
  if (bay) return bay;
  if (dock) return dock;
  return "Bay Dock";
}

export function isSlotMarkedUnavailable(slot) {
  if (!slot || typeof slot !== "object") return false;
  if (slot.unavailable === true || slot.isUnavailable === true) return true;
  if (slot.available === false) return true;
  const occ = String(slot.occupancyStatus ?? slot.occupancy ?? slot.status ?? "").toUpperCase();
  return occ.includes("UNAVAILABLE") || occ === "DISABLED" || occ === "BLOCKED";
}

export function isTrailerCheckedIn(trailer) {
  return String(trailer?.gateStatus ?? "").toUpperCase() === "CHECKED_IN";
}

function trailerFromSlot(slot) {
  if (!slot || typeof slot !== "object") return null;
  const trailerId =
    slot.occupiedByTrailerId ??
    slot.trailerId ??
    slot.trailer?.id ??
    slot.trailer?.trailerId;
  const trailerNumber =
    slot.trailerNumber ?? slot.trailerNo ?? slot.trailer?.trailerNumber ?? "";
  if (!trailerNumber && (trailerId == null || trailerId === "")) return null;
  return {
    id: trailerId,
    trailerNumber: trailerNumber || (trailerId != null ? `Trailer #${trailerId}` : ""),
    carrierName: slot.carrierName ?? slot.carrier ?? slot.trailer?.carrierName ?? "—",
    carrierOfficeCode: slot.carrierOfficeCode ?? slot.trailer?.carrierOfficeCode ?? null,
    shipmentNumber: slot.shipmentNumber ?? slot.shipmentNo ?? "—",
    shipmentType: slot.shipmentType ?? slot.direction ?? "—",
    status: slot.status ?? slot.trailer?.status ?? null,
    loadType: slot.loadType ?? slot.trailer?.loadType ?? null,
    originName: slot.originName ?? slot.trailer?.originName ?? null,
    destinationName: slot.destinationName ?? slot.trailer?.destinationName ?? null,
    checkInTime:
      slot.checkInTime ??
      slot.checkedInAt ??
      slot.checkInAt ??
      slot.gateCheckInTime ??
      null,
    // Backend-computed (TrailerServiceImpl.resolveDwellTimeDisplay) — prefer this over
    // recomputing from checkInTime client-side so this tile can never drift from the trailer
    // detail page's own "Dwell Hours", which reads the same backend value.
    dwellTime: slot.dwellTime ?? null,
    gateStatus: "CHECKED_IN",
  };
}

function findTrailerForSlot(trailers, { sectionName, bayName, dockName, slotType, slot }) {
  if (slot?.occupiedByTrailerId != null && slot.occupiedByTrailerId !== "") {
    const targetId = String(slot.occupiedByTrailerId);
    const byId = (trailers || []).find((trailer) => {
      const id = trailer?.id ?? trailer?.trailerId;
      return id != null && String(id) === targetId;
    });
    if (byId && isTrailerCheckedIn(byId)) return byId;
  }

  for (const trailer of trailers || []) {
    if (!isTrailerCheckedIn(trailer)) continue;
    const loc = parseTrailerLocationLabel(trailer.location);
    if (sectionName && loc.sectionName && !labelsMatch(sectionName, loc.sectionName)) continue;

    if (bayName && dockName) {
      if (!loc.bayName || !labelsMatch(bayName, loc.bayName)) continue;
      if (!loc.dockName || !labelsMatch(dockName, loc.dockName)) continue;
      return trailer;
    }

    if (slotType === "BAY") {
      if (!bayName) continue;
      if (!loc.bayName || !labelsMatch(bayName, loc.bayName)) continue;
      if (loc.dockName) continue;
      return trailer;
    }
    if (slotType === "DOCK") {
      if (!dockName) continue;
      if (!loc.dockName || !labelsMatch(dockName, loc.dockName)) continue;
      return trailer;
    }
  }
  return null;
}

function resolveSlotStatus(slot, trailer) {
  if (isSlotMarkedUnavailable(slot)) return YARD_SLOT_STATUS.UNAVAILABLE;
  if (trailer && isTrailerCheckedIn(trailer)) return YARD_SLOT_STATUS.OCCUPIED;
  const occ = String(slot?.occupancyStatus ?? slot?.occupancy ?? slot?.status ?? "").toUpperCase();
  if (occ.includes("OCCUPIED") || occ.includes("IN_USE")) return YARD_SLOT_STATUS.OCCUPIED;
  if (slot?.occupiedByTrailerId != null && slot.occupiedByTrailerId !== "") {
    return YARD_SLOT_STATUS.OCCUPIED;
  }
  return YARD_SLOT_STATUS.EMPTY;
}

function slotKey(sectionName, slotType, slotName) {
  return `${norm(sectionName)}|${slotType}|${normComparable(slotName)}`;
}

function pairSlotKey(sectionName, bayName, dockName) {
  return `${norm(sectionName)}|PAIR|${normComparable(bayName)}|${normComparable(dockName)}`;
}

function resolvePairStatus(baySlot, dockSlot, trailer) {
  if (isSlotMarkedUnavailable(baySlot) || isSlotMarkedUnavailable(dockSlot)) {
    return YARD_SLOT_STATUS.UNAVAILABLE;
  }
  if (trailer && isTrailerCheckedIn(trailer)) return YARD_SLOT_STATUS.OCCUPIED;
  if (resolveSlotStatus(baySlot, null) === YARD_SLOT_STATUS.OCCUPIED) return YARD_SLOT_STATUS.OCCUPIED;
  if (resolveSlotStatus(dockSlot, null) === YARD_SLOT_STATUS.OCCUPIED) return YARD_SLOT_STATUS.OCCUPIED;
  return YARD_SLOT_STATUS.EMPTY;
}

function occupiedTrailerForPair(baySlot, dockSlot, trailer) {
  if (trailer && isTrailerCheckedIn(trailer)) return trailer;
  return trailerFromSlot(baySlot) || trailerFromSlot(dockSlot);
}

/** Pair bays and docks by index within each section. */
export function getSectionBayDockPairs(section) {
  const bayNames = getSectionBayNames(section);
  const dockNames = getSectionDockNames(section);
  const count = Math.max(bayNames.length, dockNames.length);
  if (!count) return [];

  return Array.from({ length: count }, (_, index) => ({
    bayName: bayNames[index] ?? "",
    dockName: dockNames[index] ?? "",
  })).filter((pair) => pair.bayName || pair.dockName);
}

function cleanNameList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((n) => String(n ?? "").trim()).filter(Boolean);
}

/** Names from API row or legacy bays/docks counts. */
export function getSectionBayNames(section) {
  const named = cleanNameList(section?.bayNames ?? section?.bay_names);
  if (named.length) return named;
  const count = Number(section?.bays ?? section?.bayCount ?? 0);
  if (count > 0) return Array.from({ length: count }, (_, i) => `Bay ${i + 1}`);
  return [];
}

export function getSectionDockNames(section) {
  const named = cleanNameList(section?.dockNames ?? section?.dock_names);
  if (named.length) return named;
  const count = Number(section?.docks ?? section?.dockCount ?? 0);
  if (count > 0) return Array.from({ length: count }, (_, i) => `Dock ${i + 1}`);
  return [];
}

export function normalizeYardSettingsSections(settings) {
  const sections = Array.isArray(settings?.sections) ? settings.sections : [];
  return sections.map((sec, index) => {
    const name = String(sec?.name ?? sec?.sectionName ?? `Section ${index + 1}`).trim();
    const bayNames = getSectionBayNames(sec);
    const dockNames = getSectionDockNames(sec);
    return {
      ...sec,
      name,
      sectionId: sec?.sectionId ?? sec?.section_id,
      bayNames,
      dockNames,
      bays: bayNames.length,
      docks: dockNames.length,
    };
  });
}

export function normalizeYardSettingsResponse(data) {
  if (!data || typeof data !== "object") return { sections: [] };
  return {
    ...data,
    sections: normalizeYardSettingsSections(data),
  };
}

export function resolveLocationParentId(loc) {
  if (!loc) return "";
  return String(loc.id ?? loc.uid ?? loc.locationRef ?? "").trim();
}

/**
 * Build one card per bay+dock pair from yard-settings sections.
 */
export function buildYardSlotCards({ sections, locationSlots, trailers }) {
  const cards = [];
  const checkedIn = (trailers || []).filter(isTrailerCheckedIn);
  const slotIndex = buildLocationSlotIndex(locationSlots);

  const resolveSlotForCard = (sectionName, slotTypeName, slotName) => {
    const matched = matchSlotForYardSelection(locationSlots, {
      sectionName,
      bayName: slotTypeName === "BAY" ? slotName : "",
      dockName: slotTypeName === "DOCK" ? slotName : "",
    });
    if (matched.slot) return matched;

    const indexed = slotIndex.get(locationSlotKey(sectionName, slotTypeName, slotName));
    if (!indexed) return matched;

    const type = String(indexed?.type ?? indexed?.slotType ?? "").toUpperCase();
    const bid =
      indexed.currentBayId ??
      indexed.bayId ??
      (type === "BAY" ? indexed.id : undefined);
    const did =
      indexed.currentDockId ??
      indexed.dockId ??
      (type === "DOCK" ? indexed.id : undefined);
    return {
      currentBayId: bid ?? null,
      currentDockId: did ?? null,
      slot: indexed,
    };
  };

  for (const section of sections || []) {
    const sectionName = String(section?.name ?? section?.sectionName ?? "").trim();
    if (!sectionName) continue;

    for (const { bayName, dockName } of getSectionBayDockPairs(section)) {
      const bayMatch = bayName
        ? resolveSlotForCard(sectionName, "BAY", bayName)
        : { currentBayId: null, currentDockId: null, slot: null };
      const dockMatch = dockName
        ? resolveSlotForCard(sectionName, "DOCK", dockName)
        : { currentBayId: null, currentDockId: null, slot: null };

      const combinedMatch = matchSlotForYardSelection(locationSlots, {
        sectionName,
        bayName,
        dockName,
      });

      const baySlot = bayMatch.slot;
      const dockSlot = dockMatch.slot;
      const slot = combinedMatch.slot ?? baySlot ?? dockSlot;

      const trailer = findTrailerForSlot(checkedIn, {
        sectionName,
        bayName,
        dockName,
        slotType: "PAIR",
        slot,
      });
      const status = resolvePairStatus(baySlot, dockSlot, trailer);
      const occupiedTrailer =
        status === YARD_SLOT_STATUS.OCCUPIED
          ? occupiedTrailerForPair(baySlot, dockSlot, trailer)
          : null;

      const resolvedSlotType = bayName && dockName ? "PAIR" : dockName ? "DOCK" : "BAY";

      cards.push({
        id: pairSlotKey(sectionName, bayName, dockName),
        slotType: resolvedSlotType,
        sectionName,
        bayName,
        dockName,
        slotName: formatBayDockCardLabel(bayName, dockName),
        displayLabel: formatBayDockCardLabel(bayName, dockName),
        status,
        bayId: bayMatch.currentBayId ?? combinedMatch.currentBayId,
        dockId: dockMatch.currentDockId ?? combinedMatch.currentDockId,
        baySlot,
        dockSlot,
        slot,
        trailer: occupiedTrailer,
      });
    }
  }

  return cards;
}

export function yardSettingsHasLayout(settings) {
  const sections = normalizeYardSettingsSections(settings);
  return sections.some(
    (sec) => getSectionBayNames(sec).length > 0 || getSectionDockNames(sec).length > 0
  );
}

export function formatShipmentTypeDisplay(value) {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "INBOUND" || normalized.startsWith("INBOUND")) return "Inbound";
  if (normalized === "OUTBOUND" || normalized.startsWith("OUTBOUND")) return "Outbound";
  if (value === "Inbound") return "Inbound";
  if (value === "Outbound") return "Outbound";
  return value ? String(value) : "—";
}

export function formatCheckInTimeDisplay(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

/** Running dwell from check-in timestamp. */
export function formatDwellTime(checkInTime, nowMs = Date.now()) {
  if (!checkInTime) return "—";
  const start = new Date(checkInTime);
  if (Number.isNaN(start.getTime())) return "—";
  const ms = Math.max(0, nowMs - start.getTime());
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

export function isSlotAvailableForCheckIn(card) {
  if (!card) return false;
  return card.status === YARD_SLOT_STATUS.EMPTY;
}

/** True when a bay/dock option should be hidden from check-in dropdowns. */
export function isYardSelectionUnavailable(slots, { sectionName, bayName, dockName }) {
  const { slot } = matchSlotForYardSelection(slots, { sectionName, bayName, dockName });
  return isSlotMarkedUnavailable(slot);
}
